import React, { Fragment, useState, useEffect, useRef, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, } from 'reactstrap';
import { difference, last, isEmpty, sortBy, uniqBy, uniq, groupBy, keyBy, partition, } from 'lodash';
import { useDebounce, } from 'react-use';
import { toast, } from 'react-toastify';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';

import env from '../../env';
import okSoundSrc from '../../sounds/ok.mp3';
import ngSoundSrc from '../../sounds/ng.mp3';
import firebase, { functions, } from '../../firebase';
import { getDocumentData, getCollectionData, } from '../../shared/firebase';
import { safePrefix, } from '../../util';
import { labelString, nekoToggleValues, } from '../../shared/models/product';
import { idWithTenant, } from '../../shared/util';

const { entries, } = Object;
const db = firebase.firestore();
const inventoriesRef = db.collection('inventories');
const tenantsRef = db.collection('tenants');
const directShelving = functions.httpsCallable('directShelving', { timeout: 120000 });

export default function DirectShelvingModal(props) {
  const { isOpen, tenants, locations, withQuantity = false, specifiesReceivingRequestId = false, } = props;
  const tenantsById = keyBy(tenants, 'id');
  const [productBarcodeValue, setProductBarcodeValue] = useState('');
  const [quantity, setQuantity] = useState(null);
  const [debouncedProductBarcodeValue, setDebouncedProductBarcodeValue] = useState('');
  const [debouncedLocationKeyValue, setDebouncedLocationKeyValue] = useState('');
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [expirationDate, setExpirationDate] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [locationKeyValue, setLocationKeyValue] = useState('');
  const [tenantNumber, setTenantNumber] = useState('');
  const tenantId = tenantNumber && env('TENANT_PREFIX') + tenantNumber;
  const [receivingRequestNumber, setReceivingRequestNumber] = useState('');
  const receivingRequestId = receivingRequestNumber && 'W' + receivingRequestNumber;

  const calculateRows = async (allProducts) => {
    // NOTE: セット完成在庫は入荷依頼に紐づかないため、入荷依頼に割り当てると倉庫作成分が作られてしまう。同梱依頼ごとに棚入れだけ行う
    const [combinedProducts, receivingProducts] = partition(allProducts, _ => _.receivedInventory?.combiningRequestId != null && _.receivedInventory.receivingRequestId == null);
    const combinedRows = Object.values(groupBy(combinedProducts, _ => [_.ref.parent.parent.id, _.receivedInventory.combiningRequestId].join('/'))).map((products) => ({
      tenantId: products[0].ref.parent.parent.id,
      combiningRequestId: products[0].receivedInventory.combiningRequestId,
      isCombined: true,
      assignedItems: entries(groupBy(products, 'id')).map(([productId, products]) => ({
        productId,
        quantity: products.length,
        assignedProducts: products.map(_ => ({ ..._, inventoryRef: _.receivedInventory.ref, })),
      })),
    }));
    const productsGroupedByTenantId = groupBy(receivingProducts, _ => _.ref.parent.parent.id);
    const uniqTenantIds = uniq(receivingProducts.map(_ => _.ref.parent.parent.id));

    const receivingRequestsByTenant = await Promise.all(uniqTenantIds.map(async (tid) => {
      const productIds = uniq(receivingProducts.filter(_ => _.ref.parent.parent.id === tid).map(_ => _.id)).filter(_ => _);
      if(specifiesReceivingRequestId && receivingRequestId) {
        const receivingRequest = await getDocumentData(db.collection('tenants').doc(tid).collection('receivingRequests').doc(receivingRequestId));
        return receivingRequest ? [receivingRequest] : [];
      }
      if(productIds.length === 0) return [];
      const results = await Promise.all(productIds.map(productId =>
        getCollectionData(
          db.collection('tenants').doc(tid).collection('receivingRequests')
            .where('status', 'in', ['initial', 'processing'])
            .where('productIds', 'array-contains', productId)
        )
      ));
      return uniqBy(results.flat(), _ => _.id);
    }));

    const sortedReceivingRequests = sortBy(receivingRequestsByTenant.flat(), _ => _.createdAt.toDate());

    let allAssignedProducts = [];
    const newRows = [
      ...sortedReceivingRequests.map(receivingRequest => {
        const tenantProducts = productsGroupedByTenantId[receivingRequest.ref.parent.parent.id] || [];
        const notAssignedTenantProducts = difference(tenantProducts, allAssignedProducts);
        const computedItems = receivingRequest.items.map(item => {
          const { productId, quantity } = item;
          const receivedQuantity = (receivingRequest[`goodInventoryIds-${productId}`] || []).length + (receivingRequest[`ngInventoryIds-${productId}`] || []).length;
          const leftQuantity = quantity - receivedQuantity;
          const products = notAssignedTenantProducts.filter(_ => _.id === productId);
          const assignedQuantity = Math.min(leftQuantity, products.length);
          const assignedProducts = (specifiesReceivingRequestId ? products : products.filter(_ => _.receivedInventory == null || _.receivedInventory.receivingRequestId === receivingRequest.id)).slice(0, assignedQuantity);
          allAssignedProducts = [...allAssignedProducts, ...assignedProducts];
          return {
            ...item,
            assignedProducts: assignedProducts.map(_ => ({ ..._, inventoryRef: _.receivedInventory?.ref || inventoriesRef.doc(), })),
          };
        });
        const assignedItems = computedItems.filter(_ => _.assignedProducts.length > 0);
        return { ...receivingRequest, assignedItems };
      }),
      ...uniqTenantIds.map(tid => {
        const tenantProducts = productsGroupedByTenantId[tid] || [];
        const notAssignedTenantProducts = difference(tenantProducts, allAssignedProducts);
        const computedItems = entries(groupBy(notAssignedTenantProducts, 'id')).map(([productId, products]) => ({
          productId,
          quantity: products.length,
          assignedProducts: products.map(_ => ({ ..._, inventoryRef: _.receivedInventory?.ref || inventoriesRef.doc(), })),
        }));
        allAssignedProducts = [...allAssignedProducts, ...notAssignedTenantProducts];
        return { tenantId: tid, isNewForOver: true, assignedItems: computedItems };
      }),
      ...combinedRows,
    ].filter(_ => _.assignedItems.length > 0);

    return newRows;
  };

  const ngAudioEl = useRef(null);
  const okAudioEl = useRef(null);
  const productIdInput = useRef(null);
  const quantityInput = useRef(null);
  const locationInput = useRef(null);
  const playAudio = (audio) => {
    audio.currentTime = 0;
    audio.play();
  };
  useDebounce(() => {
    setDebouncedProductBarcodeValue(productBarcodeValue);
  }, 300, [productBarcodeValue]);
  useDebounce(() => {
    setDebouncedLocationKeyValue(locationKeyValue);
  }, 300, [locationKeyValue]);
  useEffect(() => {
    if(isEmpty(debouncedProductBarcodeValue)) return;

    const clear = () => {
      setProductBarcodeValue('');
      setDebouncedProductBarcodeValue('');
    };
    const isProductIdBarcode = debouncedProductBarcodeValue.match(new RegExp(`^(${env('TENANT_PREFIX')})?[^-]+-\\d+`));

    (async () => {
      const product = await (async () => {
        if(isProductIdBarcode) {
          return getDocumentData(tenantsRef.doc(safePrefix(debouncedProductBarcodeValue).match(new RegExp(`^(${env('TENANT_PREFIX')}[^-]+)`))[1]).collection('products').doc(safePrefix(debouncedProductBarcodeValue).replace(/-N$/, '').replace(new RegExp(`^${env('TENANT_PREFIX')}[^-]+-`), '')), true);
        }
        const specifiedTenantId = specifiesReceivingRequestId && (tenantNumber && env('TENANT_PREFIX') + tenantNumber);
        const fnskuQuery = specifiedTenantId
          ? tenantsRef.doc(specifiedTenantId).collection('products').where('fnsku', '==', debouncedProductBarcodeValue)
          : db.collectionGroup('products').where('fnsku', '==', debouncedProductBarcodeValue);
        const barcodeQuery = specifiedTenantId
          ? tenantsRef.doc(specifiedTenantId).collection('products').where('barcodeInfo', '==', debouncedProductBarcodeValue)
          : db.collectionGroup('products').where('barcodeInfo', '==', debouncedProductBarcodeValue);
        const [fnskuProducts, barcodeProducts] = await Promise.all([
          getCollectionData(fnskuQuery),
          getCollectionData(barcodeQuery),
        ]);
        // NOTE: 同一fnskuを持つproductが複数ある場合、受領済みinventoryのproductIdと一致するものを優先
        if(fnskuProducts.length > 0) {
          if(fnskuProducts.length === 1) return fnskuProducts[0];
          const candidates = await Promise.all(fnskuProducts.map(async (p) => {
            const tid = p.ref.parent.parent.id;
            const invs = await getCollectionData(
              db.collection('inventories')
                .where('tenantId', '==', tid)
                .where('productBarcodeInfo', '==', safePrefix(debouncedProductBarcodeValue))
                .where('productId', '==', p.id)
                .where('status', '==', 'received')
                .limit(1)
            );
            return { product: p, hasReceivedInventory: invs.length > 0 };
          }));
          return (candidates.find(_ => _.hasReceivedInventory) || candidates[0]).product;
        }
        if(barcodeProducts.length <= 1) return barcodeProducts[0];
        const results = await Promise.all(barcodeProducts.map(async (p) => {
          const rrs = await getCollectionData(
            p.ref.parent.parent.collection('receivingRequests')
              .where('status', 'in', ['initial', 'processing'])
              .where('productIds', 'array-contains', p.id)
              .limit(1)
          );
          return { product: p, hasReceivingRequest: rrs.length > 0 };
        }));
        return (results.find(_ => _.hasReceivingRequest) || results[0]).product;
      })();
      if(product == null) {
        playAudio(ngAudioEl.current);
        clear();
        return;
      }

      const isJanBarcode = !isProductIdBarcode && product.fnsku !== debouncedProductBarcodeValue;

      if(withQuantity) {
        playAudio(okAudioEl.current);
        quantityInput.current.focus();
        setCurrentProduct({ ...product, isJanBarcode });
        return;
      }

      const tenantId = product.ref.parent.parent.id;
      const sameProducts = products.filter(_ => idWithTenant(_.ref) === idWithTenant(product.ref));
      // NOTE: 在庫移動で未棚入れに戻した在庫のproductBarcodeInfoは登録時のまま(商品ラベル/JAN/FNSKU)なので、
      // 鳴かれたバーコードで突合すると空振りして新規在庫が作られてしまう。productIdで引く。
      let inventoriesRef = db.collection('inventories').where('tenantId', '==', tenantId).where('productId', '==', product.id).where('status', '==', 'received').orderBy('createdAt');
      const lastSameProductReceivedInventory = last(sameProducts.map(_ => _.receivedInventory).filter(_ => _));
      if(lastSameProductReceivedInventory?.doc != null) inventoriesRef = inventoriesRef.startAfter(lastSameProductReceivedInventory.doc);
      const inventories = await getCollectionData(inventoriesRef.limit(1), { withDoc: true });

      const newProduct = { ...product, receivedInventory: inventories[0], isJanBarcode };
      const newRows = await calculateRows([...products, newProduct]);

      const newProductWillBeIgnored = newRows.some(_ => {
        const tid = _.tenantId || _.ref.parent.parent.id;
        const tenant = tenantsById[tid];
        return _.isNewForOver && tenant?.disablesDirectShelvingWarehouseCreation &&
               _.assignedItems.some(_ => _.assignedProducts.some(_ => idWithTenant(_.ref) === idWithTenant(newProduct.ref)));
      });

      if(newProductWillBeIgnored) {
        playAudio(ngAudioEl.current);
        window.alert(`この商品は「倉庫作成分を作成しない」設定により無視されます\nテナント: ${tenantId}\n商品: ${idWithTenant(product.ref)}`);
      } else {
        playAudio(okAudioEl.current);
        setProducts([...products, newProduct]);
        setRows(newRows);
      }
      clear();
    })();
  }, [debouncedProductBarcodeValue]);
  useEffect(() => {
    if(isEmpty(debouncedLocationKeyValue)) return;

    setLocationKeyValue('');
    setDebouncedLocationKeyValue('');
    const location = locations.find(_ => _.sortKey === debouncedLocationKeyValue);
    if(location == null) return playAudio(ngAudioEl.current);
    if(!window.confirm('本当に入庫しますか？(オプションありの入荷依頼も処理されます)(ネコポスの判定等、できましたか？)')) return;

    (async () => {
      try {
        const filteredRows = rows.filter(receivingRequest => {
          const tenantId = receivingRequest.tenantId || receivingRequest.ref.parent.parent.id;
          const tenant = tenantsById[tenantId];
          return !(receivingRequest.isNewForOver && tenant?.disablesDirectShelvingWarehouseCreation);
        });

        const payload = {
          rows: filteredRows.map(receivingRequest => {
            const tenantId = receivingRequest.tenantId || receivingRequest.ref.parent.parent.id;
            return {
              tenantId,
              isNewForOver: receivingRequest.isNewForOver,
              isCombined: receivingRequest.isCombined || false,
              combiningRequestId: receivingRequest.combiningRequestId || null,
              status: receivingRequest.status,
              hasLogReceiving: receivingRequest.hasLogReceiving,
              documentNumber: receivingRequest.documentNumber,
              refPath: receivingRequest.ref?.path,
              assignedItems: receivingRequest.assignedItems.map(assignedItem => ({
                productId: assignedItem.productId,
                quantity: assignedItem.quantity,
                assignedProducts: assignedItem.assignedProducts.map(assignedProduct => ({
                  inventoryId: assignedProduct.inventoryRef.id,
                  productId: assignedProduct.id,
                  isNg: assignedProduct.receivedInventory?.isNg || false,
                  hasReceivedInventory: assignedProduct.receivedInventory != null,
                  isJanBarcode: assignedProduct.isJanBarcode,
                  barcodeInfo: assignedProduct.barcodeInfo,
                  isAmazonProduct: assignedProduct.isAmazonProduct,
                  fnsku: assignedProduct.fnsku,
                  productLabelString: labelString(assignedProduct),
                  receivedInventoryExpirationDate: assignedProduct.receivedInventory?.expirationDate?.toDate?.()?.toISOString() ?? null,
                })),
              })),
            };
          }),
          locationId: location.id,
          locationKey: location.sortKey,
          expirationDate: expirationDate?.toISOString() ?? null,
          specifiesReceivingRequestId,
          receivingRequestId,
        };

        await directShelving(payload);

        try {
          playAudio(okAudioEl.current);
        } catch(e) { }
        setProducts([]);
        setRows([]);
        setExpirationDate(null);
        productIdInput.current.focus();
        toast.success(`入庫しました`);
      } catch(e) {
        console.error(e);
        toast.error(`失敗しました。不正なデータが作られた可能性があります。${e.message}`);
      }
    })();
  }, [debouncedLocationKeyValue]);
  const onClickDone = async () => {
    const tenantId = currentProduct.ref.parent.parent.id;
    const sameProducts = products.filter(_ => idWithTenant(_.ref) === idWithTenant(currentProduct.ref));
    let inventoriesRef = db.collection('inventories').where('tenantId', '==', tenantId).where('productId', '==', currentProduct.id).where('status', '==', 'received').orderBy('createdAt');
    const lastSameProductReceivedInventory = last(sameProducts.map(_ => _.receivedInventory).filter(_ => _));
    if(lastSameProductReceivedInventory?.doc != null) inventoriesRef = inventoriesRef.startAfter(lastSameProductReceivedInventory.doc);
    const inventories = await getCollectionData(inventoriesRef.limit(quantity), { withDoc: true });

    const newProducts = Array(quantity).fill(null).map((_, i) => ({ ...currentProduct, receivedInventory: inventories[i], }));
    const allProducts = [...products, ...newProducts];
    const newRows = await calculateRows(allProducts);

    const newProductsWillBeIgnored = newRows.some(_ => {
      const tid = _.tenantId || _.ref.parent.parent.id;
      const tenant = tenantsById[tid];
      return _.isNewForOver && tenant?.disablesDirectShelvingWarehouseCreation &&
             _.assignedItems.some(_ => _.assignedProducts.some(_ => newProducts.some(np => idWithTenant(_.ref) === idWithTenant(np.ref))));
    });

    if(newProductsWillBeIgnored) {
      playAudio(ngAudioEl.current);
      window.alert(`この商品は「倉庫作成分を作成しない」設定により無視されます\nテナント: ${tenantId}\n商品: ${idWithTenant(currentProduct.ref)}`);
    } else {
      setProducts(allProducts);
      setRows(newRows);
      locationInput.current.focus();
    }

    setProductBarcodeValue('');
    setDebouncedProductBarcodeValue('');
    setCurrentProduct(null);
    setQuantity(null);
    productIdInput.current.focus();
  };

  return (
    <Modal isOpen={isOpen}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            {specifiesReceivingRequestId ? '入荷依頼ID指定' : withQuantity ? '数値入力' : ''}ダイレクト入庫
          </h5>
        </div>
      </div>
      <ModalBody>
        {
          specifiesReceivingRequestId && (
            <FormGroup className="d-flex align-items-center">
              <InputGroup>
                <div className="input-group-prepend">
                  <span className="input-group-text">{env('TENANT_PREFIX')}</span>
                </div>
                <Input value={tenantNumber} onChange={_ => setTenantNumber(_.target.value)} type="tel" />
              </InputGroup>
              <div className="mx-2">-</div>
              <InputGroup>
                <div className="input-group-prepend">
                  <span className="input-group-text">W</span>
                </div>
                <Input value={receivingRequestNumber} onChange={_ => setReceivingRequestNumber(_.target.value)} type="tel" />
              </InputGroup>
            </FormGroup>
          )
        }
        <div className="d-flex gap-1 justify-content-between align-items-end">
          <FormGroup className="flex-fill m-0">
            <Label>商品バーコード</Label>
            <Input innerRef={productIdInput} value={productBarcodeValue} onChange={_ => setProductBarcodeValue(_.target.value)} />
          </FormGroup>
          <FormGroup className={classnames('m-0', { 'd-none': !withQuantity })} style={{ width: 80 }}>
            <Label>数量</Label>
            <Input type="number" innerRef={quantityInput} className="text-right" step="1" value={quantity ?? ''} onChange={_ => setQuantity(parseInt(_.target.value, 10))} />
          </FormGroup>
          <Button className={classnames({ 'd-none': !withQuantity })} onClick={onClickDone} disabled={!(0 < quantity && quantity < 10000)}>
            完了
          </Button>
        </div>
        <FormGroup className="mt-2">
          <Label>期限:</Label>
          <DatePicker dateFormat="yyyy/MM/dd" selected={expirationDate} onChange={_ => setExpirationDate(_)} isClearable />
        </FormGroup>
        <FormGroup>
          <Label>ロケーション</Label>
          <Input innerRef={locationInput} value={locationKeyValue} onChange={_ => setLocationKeyValue(_.target.value)} disabled={products.length === 0} />
        </FormGroup>
        <hr className="my-5" />
        <h5>
          現在のアイテム
        </h5>
        {
          rows.length > 0 && (() => {
            const totalAssignedCount = rows.reduce((x, row) =>
              x + row.assignedItems.reduce((y, item) => y + item.assignedProducts.length, 0), 0);
            const totalLeftCount = rows.reduce((x, row) => {
              return x + row.assignedItems.reduce((y, assignedItem) => {
                const { productId } = assignedItem;
                const leftCount = (row[`goodReceivedInventoryIds-${productId}`] || []).length +
                                  (row[`ngReceivedInventoryIds-${productId}`] || []).length -
                                  (row[`goodInventoryIds-${productId}`] || []).length -
                                  (row[`ngInventoryIds-${productId}`] || []).length;
                return y + leftCount;
              }, 0);
            }, 0);
            return (
              <div className="mb-2 p-2 bg-light border rounded">
                <strong>合計:</strong> 数量 {totalAssignedCount} / 検品済未入庫数 {totalLeftCount}
              </div>
            );
          })()
        }
        <div>
          <table className="table table-bordered">
            <thead className="thead-light">
              <tr>
                <th style={{ minWidth: 80 }}>入荷依頼ID</th>
                <th>商品</th>
                <th style={{ minWidth: 80, }}>
                  数量/検品済未入庫数
                </th>
              </tr>
            </thead>
            <tbody>
              {
                rows.map((row) => {
                  const { ref, assignedItems, receivingOptionValues, isNewForOver, isCombined, combiningRequestId, } = row;
                  const tenantId = row.tenantId || ref.parent.parent.id;
                  const displayId = isCombined ? `${tenantId}-${combiningRequestId}` : ref != null ? idWithTenant(ref) : `${tenantId}-`;
                  const tenant = tenantsById[tenantId];
                  const willBeIgnored = isNewForOver && tenant?.disablesDirectShelvingWarehouseCreation;

                  return (
                    <Fragment key={displayId}>
                      {
                        assignedItems.map((assignedItem, i) => {
                          const { productId, assignedProducts, } = assignedItem;
                          const leftCount = (row[`goodReceivedInventoryIds-${productId}`] || []).length + (row[`ngReceivedInventoryIds-${productId}`] || []).length - (row[`goodInventoryIds-${productId}`] || []).length - (row[`ngInventoryIds-${productId}`] || []).length;
                          const [product] = assignedProducts;
                          const onClickNeko = async () => {
                            const values = nekoToggleValues(product);
                            const suffix = ({ true: 'に指定', false: '指定を解除' })[values.isNeko];
                            if(!window.confirm(`本当にネコポス${suffix}しますか？`)) return;

                            await product.ref.update(values);
                            const isTarget = _ => idWithTenant(_.ref) === idWithTenant(product.ref);
                            setProducts(products.map(_ => isTarget(_) ? { ..._, ...values } : _));
                            setRows(rows.map(row => ({
                              ...row,
                              assignedItems: row.assignedItems.map(assignedItem => ({
                                ...assignedItem,
                                assignedProducts: assignedItem.assignedProducts.map(_ => isTarget(_) ? { ..._, ...values } : _),
                              })),
                            })));
                          };
                          return (
                            <tr key={i}>
                              {
                                i === 0 && (
                                  <td rowSpan={assignedItems.length}>
                                    {displayId}
                                    {!isEmpty(receivingOptionValues) && <div className="badge badge-danger">オプションあり</div>}
                                    {willBeIgnored && <div className="badge badge-warning">無視される</div>}
                                    {isCombined && <div className="badge badge-info">セット完成品</div>}
                                  </td>
                                )
                              }
                              <td>
                                <div className="d-flex gap-2">
                                  <div>
                                    <div className="d-flex gap-2 justify-content-between">
                                      <div>
                                        {idWithTenant(product.ref)}
                                      </div>
                                      <div>
                                        <span className={classnames('fas fa-cat cursor-pointer', { 'text-info': product.isNeko, 'text-muted': !product.isNeko })} onClick={onClickNeko} />
                                        {!product.isNeko && product.requestsNeko && <span className="text-danger">希望</span>}
                                      </div>
                                    </div>
                                    <div style={{ wordBreak: 'break-all', }}>{product.name}</div>
                                    <div className="text-muted small">{product.barcodeInfo}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{assignedProducts.length}/{isCombined ? '-' : leftCount}</td>
                            </tr>
                          );
                        })
                      }
                    </Fragment>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter>
        {
          products.length === 0 && (
            <Button className="cancel" color="secondary" onClick={props.onClickClose}>閉じる</Button>
          )
        }
      </ModalFooter>
      <audio src={ngSoundSrc} ref={ngAudioEl} />
      <audio src={okSoundSrc} ref={okAudioEl} />
    </Modal>
  );
};

